import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../Button";
import IconTile, { IconTileTone } from "../../IconTile";
import ModalBalcaoVirtual from "../../ModalBalcaoVirtual";

import style from "./styles.module.scss";

const keyI18n = "translations.home.onlineServices";

type ActionVariant = "outlined" | "elevated";

interface ServiceAction {
  labelKey: string;
  variant: ActionVariant;
  trailingIcon: string;
  href?: string;
  target?: "_blank";
  action?: "virtualDesk";
}

interface Service {
  name: string;
  icon: string;
  tone: IconTileTone;
  titleKey: string;
  descriptionKey: string;
  highlighted?: boolean;
  actions: ServiceAction[];
}

const services: Service[] = [
  {
    name: "allServices",
    icon: "apps",
    tone: "secondary",
    titleKey: `${keyI18n}.allServices.title`,
    descriptionKey: `${keyI18n}.allServices.description`,
    actions: [
      {
        labelKey: `${keyI18n}.allServices.actions.access`,
        variant: "outlined",
        trailingIcon: "arrow_forward",
        href: "/servicos",
      },
    ],
  },
  {
    name: "virtualDesk",
    icon: "support_agent",
    tone: "primary",
    titleKey: `${keyI18n}.virtualDesk.title`,
    descriptionKey: `${keyI18n}.virtualDesk.description`,
    highlighted: true,
    actions: [
      {
        labelKey: `${keyI18n}.virtualDesk.actions.rooms`,
        variant: "elevated",
        trailingIcon: "arrow_outward",
        action: "virtualDesk",
      },
    ],
  },
  {
    name: "debtsApp",
    icon: "smartphone",
    tone: "tertiary",
    titleKey: `${keyI18n}.debtsApp.title`,
    descriptionKey: `${keyI18n}.debtsApp.description`,
    actions: [
      {
        labelKey: `${keyI18n}.debtsApp.actions.android`,
        variant: "outlined",
        trailingIcon: "open_in_new",
        href:
          "https://play.google.com/store/apps/details?id=br.gov.ce.fortaleza.pgm.dividaativa",
        target: "_blank",
      },
      {
        labelKey: `${keyI18n}.debtsApp.actions.ios`,
        variant: "outlined",
        trailingIcon: "open_in_new",
        href: "https://apps.apple.com/br/app/d%C3%ADvida-ativa-fortaleza/id1499380698",
        target: "_blank",
      },
    ],
  },
];

export default function OnlineServices() {
  const { t } = useTranslation();
  const [showVirtualDesk, setShowVirtualDesk] = useState(false);

  return (
    <>
      <section className={style.onlineServices} id="servicos-online">
        <header className={style.header}>
          <p className={`pgm-label-large ${style.overline}`}>
            {t(`${keyI18n}.overline`)}
          </p>
          <h2 className={`pgm-title-large ${style.subtitle}`}>
            {t(`${keyI18n}.title`)}
          </h2>
        </header>

        <div className={style.list}>
          {services.map((service) => (
            <article className={style.card} key={service.name}>
              <IconTile icon={service.icon} tone={service.tone} />

              {service.highlighted && (
                <span className={`pgm-label-large ${style.badge}`}>
                  {t(`${keyI18n}.recommended`)}
                </span>
              )}

              <div className={style.content}>
                <h3 className={`pgm-headline-medium ${style.cardTitle}`}>
                  {t(service.titleKey)}
                </h3>
                <p className={`pgm-body-large ${style.cardDescription}`}>
                  {t(service.descriptionKey)}
                </p>
              </div>

              <div className={style.cardActions}>
                {service.actions.map(
                  ({ labelKey, variant, trailingIcon, href, target, action }) => (
                    <Button
                      href={href}
                      key={labelKey}
                      onClick={
                        action === "virtualDesk"
                          ? () => setShowVirtualDesk(true)
                          : undefined
                      }
                      target={target}
                      trailingIcon={
                        <span className="material-symbols-sharp">{trailingIcon}</span>
                      }
                      variant={variant}
                    >
                      {t(labelKey)}
                    </Button>
                  ),
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      <ModalBalcaoVirtual
        onHide={() => setShowVirtualDesk(false)}
        show={showVirtualDesk}
      />
    </>
  );
}
